import { useState } from "react";
import { Link, useNavigate } from "react-router";
import { authClient, useSession } from "../lib/auth";
import { RedirectTo } from "../components/RedirectTo";

export function Signup() {
  const { data: session, isPending: sessionPending } = useSession();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [pending, setPending] = useState(false);
  const [oauthPending, setOauthPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  if (sessionPending) return null;
  if (session?.user?.id) return <RedirectTo to="/dashboard" />;

  // Better Auth redirects with the raw callbackURL string; relative paths
  // resolve against the API origin (api.klemento.com), so any flow that
  // hops through the API (OAuth) needs an absolute URL pinned to the app
  // origin.
  const callbackAbsolute = `${window.location.origin}/dashboard`;

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setPending(true);
    try {
      const result = await authClient.signUp.email({ name, email, password, callbackURL: callbackAbsolute });
      if (result.error) {
        throw new Error(result.error.message ?? "Could not create your account.");
      }
      navigate("/dashboard", { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong. Try again.");
    } finally {
      setPending(false);
    }
  }

  async function onGoogle() {
    setError(null);
    setOauthPending(true);
    try {
      const result = await authClient.signIn.social({ provider: "google", callbackURL: callbackAbsolute });
      if (result.error) {
        throw new Error(result.error.message ?? "Could not start Google sign-up.");
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong. Try again.");
      setOauthPending(false);
    }
  }

  return (
    <div style={{ maxWidth: 420, margin: "0 auto" }}>
      <h1 style={{ fontSize: 32, fontWeight: 700, letterSpacing: "-0.03em", margin: "0 0 8px" }}>Create an account</h1>
      <p style={{ color: "rgb(var(--color-ink-muted))", margin: "0 0 24px" }}>
        3 free analyses every month. Upgrade for unlimited and trends.
      </p>

      {error ? (
        <div
          role="alert"
          style={{
            background: "rgb(var(--color-warning) / 0.08)",
            border: "1px solid rgb(var(--color-warning) / 0.3)",
            color: "rgb(var(--color-warning))",
            padding: "10px 14px",
            borderRadius: 10,
            marginBottom: 14,
            fontSize: 14,
          }}
        >
          {error}
        </div>
      ) : null}

      <button
        type="button"
        onClick={onGoogle}
        disabled={oauthPending || pending}
        className="btn btn-secondary"
        style={{ width: "100%", marginBottom: 14 }}
      >
        <GoogleIcon /> {oauthPending ? "..." : "Continue with Google"}
      </button>
      <Divider />

      <form onSubmit={onSubmit} style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        <input
          className="input"
          type="text"
          placeholder="Your name"
          required
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <input
          className="input"
          type="email"
          placeholder="you@work.com"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        <input
          className="input"
          type="password"
          placeholder="Choose a password (10+ characters)"
          required
          minLength={10}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
        <button type="submit" className="btn btn-primary" disabled={pending || oauthPending}>
          {pending ? "..." : "Create account"}
        </button>
      </form>
      <p style={{ marginTop: 24, fontSize: 14, color: "rgb(var(--color-ink-muted))" }}>
        Already have one? <Link to="/login" style={{ textDecoration: "underline" }}>Sign in</Link>.
      </p>
    </div>
  );
}

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
      <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" />
      <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z" />
      <path fill="#FBBC05" d="M3.964 10.706A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.706V4.962H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.038l3.007-2.332z" />
      <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.962L3.964 7.294C4.672 5.167 6.656 3.58 9 3.58z" />
    </svg>
  );
}

function Divider() {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 12,
        margin: "0 0 14px",
        color: "rgb(var(--color-ink-muted))",
        fontSize: 13,
      }}
    >
      <div style={{ flex: 1, height: 1, background: "rgb(var(--color-border-strong))" }} />
      <span>or</span>
      <div style={{ flex: 1, height: 1, background: "rgb(var(--color-border-strong))" }} />
    </div>
  );
}
